/**
 * Face-edge kernel — planar face tracing (K0).
 *
 * The heart of both face SPLITTING and loop HEALING (spike §4.1): given a planar
 * graph (nodes + non-crossing undirected edges, ids preserved), trace every face
 * of the subdivision via angular half-edge traversal, keep the bounded (CCW)
 * cycles, strip out-and-back spurs (a dangling edge on a face does not split it —
 * SketchUp exactly), and assign HOLES across disconnected components by geometric
 * containment (a loop floating inside a face becomes that face's hole).
 *
 * Input contract: edges never cross away from shared nodes (the arrangement in
 * arrange.ts guarantees it before calling here). Output loops reference the input
 * edge ids with traversal directions — exactly the stored BodyLoop shape.
 */

import type { Pt2 } from './geom3'
import { AREA_EPS } from './geom3'
import { polygonArea, pointInRing } from './topo'

export interface TraceNode { id: string; pt: Pt2 }
export interface TraceEdge { id: string; a: string; b: string }

export interface TraceLoop {
  edges: string[]
  dirs: boolean[]
  /** ordered ring: the tail node of each traversed edge */
  pts: Pt2[]
  area: number
}

export interface TracedFace {
  outer: TraceLoop
  holes: TraceLoop[]
}

interface Half {
  eid: string
  tail: string
  head: string
  dir: boolean          // true = traversing the input edge a→b
  angle: number         // direction angle at the tail
  twin: number          // index of the reverse half-edge
  next: number          // face-traversal successor (filled in pass 2)
  visited: boolean
  comp: number          // connected component index (by node)
}

export function traceFaces(nodes: TraceNode[], edges: TraceEdge[]): TracedFace[] {
  if (!edges.length) return []
  const nodeById = new Map(nodes.map((n) => [n.id, n.pt]))

  // union-find over nodes → components (holes nest ACROSS components)
  const parent = new Map<string, string>()
  const find = (x: string): string => {
    let r = x
    while (parent.get(r) !== r) r = parent.get(r)!
    let c = x
    while (parent.get(c) !== c) { const n = parent.get(c)!; parent.set(c, r); c = n }
    return r
  }
  for (const n of nodes) parent.set(n.id, n.id)
  for (const e of edges) {
    const ra = find(e.a)
    const rb = find(e.b)
    if (ra !== rb) parent.set(ra, rb)
  }
  const compIndex = new Map<string, number>()
  const compOf = (nid: string): number => {
    const root = find(nid)
    if (!compIndex.has(root)) compIndex.set(root, compIndex.size)
    return compIndex.get(root)!
  }

  // half-edges
  const halves: Half[] = []
  for (const e of edges) {
    const pa = nodeById.get(e.a)!
    const pb = nodeById.get(e.b)!
    const i = halves.length
    halves.push({
      eid: e.id, tail: e.a, head: e.b, dir: true,
      angle: Math.atan2(pb.y - pa.y, pb.x - pa.x),
      twin: i + 1, next: -1, visited: false, comp: compOf(e.a),
    })
    halves.push({
      eid: e.id, tail: e.b, head: e.a, dir: false,
      angle: Math.atan2(pa.y - pb.y, pa.x - pb.x),
      twin: i, next: -1, visited: false, comp: compOf(e.a),
    })
  }

  // per-node CCW-sorted outgoing lists → next(h) = predecessor of twin(h) in CCW
  // order at h's head (the leftmost-turn rule: bounded faces trace CCW)
  const outgoing = new Map<string, number[]>()
  for (let i = 0; i < halves.length; i++) {
    const list = outgoing.get(halves[i].tail) || []
    list.push(i)
    outgoing.set(halves[i].tail, list)
  }
  for (const list of outgoing.values()) list.sort((i, j) => halves[i].angle - halves[j].angle)
  for (let i = 0; i < halves.length; i++) {
    const twin = halves[i].twin
    const list = outgoing.get(halves[twin].tail)!
    const pos = list.indexOf(twin)
    halves[i].next = list[(pos - 1 + list.length) % list.length]
  }

  // trace cycles
  interface Cycle { loop: TraceLoop; comp: number }
  const bounded: Cycle[] = []
  const outers: Cycle[] = []     // each component's unbounded contour (negative area)
  for (let i = 0; i < halves.length; i++) {
    if (halves[i].visited) continue
    const eids: string[] = []
    const dirs: boolean[] = []
    const pts: Pt2[] = []
    let h = i
    do {
      const half = halves[h]
      half.visited = true
      eids.push(half.eid)
      dirs.push(half.dir)
      pts.push(nodeById.get(half.tail)!)
      h = half.next
    } while (h !== i)
    const stripped = stripSpurs(eids, dirs, pts)
    if (!stripped) continue // pure tree — no face
    const area = polygonArea(stripped.pts)
    const cyc: Cycle = { loop: { ...stripped, area }, comp: halves[i].comp }
    if (area > AREA_EPS) bounded.push(cyc)
    else if (area < -AREA_EPS) outers.push(cyc)
  }

  // hole nesting: a component's outer contour contained in a bounded face of a
  // DIFFERENT component becomes that face's hole — assigned to the SMALLEST
  // containing face so nesting depth resolves correctly
  const faces: TracedFace[] = bounded.map((b) => ({ outer: b.loop, holes: [] }))
  const boundedMeta = bounded.map((b, i) => ({ i, comp: b.comp, area: b.loop.area }))
  for (const o of outers) {
    const probe = o.loop.pts[0]
    let best = -1
    let bestArea = Infinity
    for (const m of boundedMeta) {
      if (m.comp === o.comp) continue
      if (m.area < bestArea && pointInRing(probe, bounded[m.i].loop.pts)) {
        // must not sit inside one of that face's already-assigned SAME-component
        // sibling regions — smallest-area containment resolves it
        best = m.i
        bestArea = m.area
      }
    }
    if (best >= 0) faces[best].holes.push(o.loop)
  }
  return faces
}

/** remove out-and-back spur traversals (same edge, opposite dirs, adjacent —
 *  cyclically) until none remain; null when the cycle is a pure tree walk */
function stripSpurs(eids: string[], dirs: boolean[], pts: Pt2[]): { edges: string[]; dirs: boolean[]; pts: Pt2[] } | null {
  const e = [...eids]
  const d = [...dirs]
  const p = [...pts]
  let changed = true
  while (changed && e.length) {
    changed = false
    for (let i = 0; i < e.length; i++) {
      const j = (i + 1) % e.length
      if (i !== j && e[i] === e[j] && d[i] !== d[j]) {
        // remove both traversals; the point at j is the spur tip
        const removeHigh = Math.max(i, j)
        const removeLow = Math.min(i, j)
        e.splice(removeHigh, 1); d.splice(removeHigh, 1); p.splice(removeHigh, 1)
        e.splice(removeLow, 1); d.splice(removeLow, 1); p.splice(removeLow, 1)
        changed = true
        break
      }
    }
  }
  if (e.length < 3) return null
  return { edges: e, dirs: d, pts: p }
}
